import { useNavigate } from 'react-router-dom';
import { Scale } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBranding } from '@/hooks/useBranding';
import { Skeleton } from '@/components/ui/skeleton';

interface AppLogoProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  linkTo?: string;
  showSubtitle?: boolean;
}

const sizeClasses = {
  sm: 'h-8 max-w-[120px]',
  md: 'h-10 max-w-[160px]',
  lg: 'h-20 max-w-[280px]',
};

const iconSizes = {
  sm: 'h-7 w-7',
  md: 'h-9 w-9',
  lg: 'h-14 w-14',
};

const textSizes = {
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-3xl',
};

export function AppLogo({ 
  size = 'md', 
  className, 
  linkTo,
  showSubtitle = false 
}: AppLogoProps) {
  const { currentLogo, logoAltText, isLoading } = useBranding();
  const navigate = useNavigate();

  const handleClick = () => {
    if (linkTo) {
      navigate(linkTo);
    }
  };

  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Skeleton className={cn("rounded-lg", iconSizes[size])} />
        <Skeleton className="h-4 w-20" />
      </div>
    );
  }

  const content = currentLogo ? (
    <div className={cn("flex items-center gap-2", className)}>
      <img
        src={currentLogo}
        alt={logoAltText}
        className={cn("object-contain", sizeClasses[size])}
      />
      {showSubtitle && (
        <p className="text-xs text-muted-foreground">Case Manager</p>
      )}
    </div>
  ) : (
    <div className={cn("flex items-center gap-2", className)}>
      <div className={cn(
        "flex items-center justify-center rounded-lg gradient-gold",
        iconSizes[size]
      )}>
        <Scale className={cn("text-primary", size === 'lg' ? 'h-8 w-8' : size === 'md' ? 'h-5 w-5' : 'h-4 w-4')} />
      </div>
      <div>
        <h1 className={cn("font-bold text-foreground", textSizes[size])}>
          NORIX
        </h1>
        {showSubtitle && (
          <p className="text-xs text-muted-foreground">Case Manager</p>
        )}
      </div>
    </div>
  );

  if (linkTo) {
    return (
      <button 
        onClick={handleClick} 
        className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
      >
        {content}
      </button>
    );
  }

  return content;
}
